import { createShallowStore } from "replo-utils/react/zustand";

// NOTE (Andrew, 2026-01-26, REPL-22179): Metadata captured at publish time to avoid stale closures
// in mutation callbacks when user switches pages during publish
type DeploymentMetadata = {
  routeType: "funnel" | "page";
  publishedSiteUrl: string;
};
// Note (Andrew, 2026-02-02, REPL-22179):
// DeploymentKeyParts is meant to be an array of the fileIds being published.
type DeploymentKeyParts = readonly string[];

const toDeploymentStoreKey = (parts: DeploymentKeyParts): string => {
  return JSON.stringify(parts);
};

type DeploymentState =
  | { status: "publishing"; startTime: number; metadata: DeploymentMetadata }
  | { status: "success"; metadata: DeploymentMetadata }
  | { status: "error" };

interface DeploymentStateStore {
  deployments: Record<string, DeploymentState>;
  actions: {
    setPublishing: (
      keyParts: DeploymentKeyParts,
      startTime: number,
      metadata: DeploymentMetadata,
    ) => void;
    setSuccess: (keyParts: DeploymentKeyParts) => void;
    setError: (keyParts: DeploymentKeyParts) => void;
    clearDeployment: (keyParts: DeploymentKeyParts) => void;
  };
}

// NOTE (Andrew, 2026-01-26, REPL-22179): Persists deployment state across component remounts
// so mutation callbacks can update UI even after page switching causes remounts.
const useDeploymentStateStore = createShallowStore<DeploymentStateStore>(
  (set) => ({
    deployments: {},
    actions: {
      setPublishing: (keyParts, startTime, metadata) => {
        return set((state) => ({
          deployments: {
            ...state.deployments,
            [toDeploymentStoreKey(keyParts)]: {
              status: "publishing",
              startTime,
              metadata,
            },
          },
        }));
      },
      setSuccess: (keyParts) => {
        return set((state) => {
          const key = toDeploymentStoreKey(keyParts);
          const existing = state.deployments[key];
          // NOTE (Andrew, 2026-01-26, REPL-22179): Preserve metadata from publishing state.
          // If no publishing state exists, this is a no-op (shouldn't happen in practice).
          if (existing?.status !== "publishing") {
            return state;
          }
          return {
            deployments: {
              ...state.deployments,
              [key]: { status: "success", metadata: existing.metadata },
            },
          };
        });
      },
      setError: (keyParts) => {
        return set((state) => ({
          deployments: {
            ...state.deployments,
            [toDeploymentStoreKey(keyParts)]: { status: "error" },
          },
        }));
      },
      clearDeployment: (keyParts) => {
        return set((state) => {
          const { [toDeploymentStoreKey(keyParts)]: _, ...rest } =
            state.deployments;
          return { deployments: rest };
        });
      },
    },
  }),
);

// NOTE (Andrew, 2026-02-02, REPL-22179): Pure selector that takes state and keyParts.
// Used by both the React hook and imperative callers.
const getDeployment = (
  state: DeploymentStateStore,
  keyParts: DeploymentKeyParts,
): DeploymentState | undefined => {
  return state.deployments[toDeploymentStoreKey(keyParts)];
};

export const useDeploymentState = (keyParts: DeploymentKeyParts) => {
  return useDeploymentStateStore((state) => getDeployment(state, keyParts));
};

export const useDeploymentStateActions = () => {
  return useDeploymentStateStore((state) => state.actions);
};
